import { useKeybindingContext } from '../hooks/keybinding/KeybindingContext.jsx';
import { formatKeyDisplay } from '../hooks/keybinding/utils.js';

/**
 * KeySequenceIndicator - Shows current key sequence being built
 * 
 * Displays a small overlay when user is building a multi-key sequence,
 * showing the current progress and providing a visual escape hint.
 */
const KeySequenceIndicator = () => {
  const { isBuilding, currentSequence, resetSequence } = useKeybindingContext();
  
  // Temporarily hidden for user testing
  return null;
  
  // Don't render if not building or no sequence
  if (!isBuilding || currentSequence.length === 0) {
    return null;
  }
  
  const handleEscapeClick = () => {
    resetSequence();
  };
  
  return (
    <div className="fixed bottom-spacing-4 right-spacing-4 z-50 animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
      <div className="bg-bg-tooltip border border-border-default rounded-lg px-spacing-3 py-spacing-2 shadow-lg backdrop-blur-sm">
        <div className="flex items-center gap-spacing-2">
          {/* Key sequence display */}
          <div className="flex items-center gap-spacing-1">
            {currentSequence.map((key, index) => (
              <div key={index} className="flex items-center gap-spacing-1">
                {index > 0 && (
                  <span className="text-secondary text-[10px]" aria-hidden="true">
                    →
                  </span>
                )}
                <kbd className="inline-flex items-center px-spacing-1 py-0.5 bg-bg-well border border-border-default rounded text-[11px] font-mono text-primary min-w-[20px] justify-center">
                  {formatKeyDisplay(key)}
                </kbd>
              </div>
            ))}
            
            {/* Waiting indicator */}
            <span className="text-secondary text-[10px] ml-spacing-1" aria-hidden="true">
              ...
            </span>
          </div>
          
          {/* Escape hint */}
          <div className="flex items-center gap-spacing-1 pl-spacing-2 border-l border-border-divider">
            <button
              onClick={handleEscapeClick}
              className="flex items-center gap-spacing-1 text-secondary hover:text-primary transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-border focus-visible:ring-inset rounded px-spacing-1 py-0.5"
              aria-label="Cancel key sequence"
              title="Press Escape to cancel"
            >
              <kbd className="text-[10px] font-mono">ESC</kbd>
              <span className="text-[10px]">cancel</span>
            </button>
          </div>
        </div>
        
        {/* Screen reader description */}
        <div className="sr-only">
          Key sequence in progress: {currentSequence.join(', then ')}.
          Press Escape to cancel or continue typing to complete the sequence.
        </div>
      </div>
    </div>
  );
};

export default KeySequenceIndicator;